/**
 * services/sync/SyncImportContext.ts
 * Transaction-scoped context for sync operations
 *
 * Provides mechanisms for:
 * 1. Import suppression - detect when code runs within a sync import transaction
 *    to suppress operation logging (we don't re-log remote operations as local)
 * 2. Instrumented write detection - detect when writes go through instrumented
 *    paths (BaseService, persistOnboardingData) vs direct db.table access
 *
 * How it works:
 * - SyncEngine._doImport() marks transactions with `isImport: true`
 * - BaseService/persistOnboardingData mark transactions with `isInstrumented: true`
 * - SyncOperationService checks `isImportContext()` before logging operations
 * - Dexie hooks check both contexts to warn on uninstrumented direct writes
 *
 * Why transaction-scoped (not module-level flag):
 * - Module-level flags can bleed across async operations
 * - Transaction context is automatically scoped to the Dexie transaction
 * - Safe for concurrent operations (e.g., local write during import)
 * - No risk of forgetting to reset flag after import
 *
 * This module is intentionally minimal to avoid circular dependencies.
 * It only imports Dexie and exports pure functions.
 */

import Dexie from 'dexie';

/**
 * Extended transaction type with sync context flags.
 *
 * Dexie's Transaction type doesn't include custom properties,
 * so we use a type assertion to access our flags.
 *
 * Properties:
 * - `isImport`: Set by SyncEngine during import operations
 * - `isInstrumented`: Set by BaseService/persistOnboardingData for op-logged writes
 * - `_warnedTables`: Used by hooks to dedupe warnings (one per table per transaction)
 */
export interface SyncTransaction {
  isImport?: boolean;
  isInstrumented?: boolean;
  _warnedTables?: Set<string>;
}

/**
 * Check if the current code is running within an import transaction.
 *
 * Returns true if we're inside a Dexie transaction that has been
 * marked with `isImport: true` by SyncEngine._doImport().
 *
 * Use this to suppress operation logging during imports - remote
 * operations should not be re-logged as local operations.
 *
 * @returns true if in import context, false otherwise
 *
 * @example
 * ```ts
 * // In SyncOperationService.logOperation():
 * if (isImportContext()) {
 *   return null; // Suppress logging during import
 * }
 * ```
 */
export function isImportContext(): boolean {
  const trans = Dexie.currentTransaction as unknown as SyncTransaction | undefined;
  return trans?.isImport === true;
}

/**
 * Mark the current transaction as an import context.
 *
 * Call this at the START of a Dexie transaction callback to mark
 * all code within the transaction as running in import context.
 * This suppresses operation logging for the duration of the transaction.
 *
 * IMPORTANT: Must be called inside an active Dexie transaction.
 * If called outside a transaction, this is a no-op with a warning.
 *
 * @example
 * ```ts
 * await db.transaction('rw', tables, async () => {
 *   markAsImportContext(); // First line of transaction
 *   await this.importBaseline(baseline);
 *   await this.importDelta(delta);
 * });
 * ```
 */
export function markAsImportContext(): void {
  const trans = Dexie.currentTransaction as unknown as SyncTransaction | undefined;
  if (trans) {
    trans.isImport = true;
  } else {
    console.warn('[SyncImportContext] markAsImportContext() called outside of transaction');
  }
}

/**
 * Check if a transaction is marked as an instrumented write context.
 *
 * Returns true if the transaction has been marked with `isInstrumented: true`
 * by BaseService or persistOnboardingData. Used by Dexie hooks to avoid
 * warning on writes that already have proper op logging.
 *
 * @param trans - Transaction object passed to Dexie hooks (will be cast to SyncTransaction)
 * @returns true if in instrumented write context, false otherwise
 *
 * @example
 * ```ts
 * // In Dexie hook:
 * table.hook('creating', function(primKey, obj, trans) {
 *   if (!isInstrumentedWriteContext(trans) && !isImportContext()) {
 *     console.warn('Direct write detected...');
 *   }
 * });
 * ```
 */
export function isInstrumentedWriteContext(trans: unknown): boolean {
  const syncTrans = trans as SyncTransaction | undefined;
  return syncTrans?.isInstrumented === true;
}

/**
 * Mark the current transaction as an instrumented write context.
 *
 * Call this at the START of a Dexie transaction callback in code paths
 * that properly handle op logging (BaseService methods, persistOnboardingData).
 * This prevents safety-net hooks from warning on these writes.
 *
 * IMPORTANT: Must be called inside an active Dexie transaction.
 * If called outside a transaction, this is a no-op with a warning.
 *
 * @example
 * ```ts
 * await db.transaction('rw', tables, async () => {
 *   markAsInstrumentedWriteContext(); // First line of transaction
 *   await this.table.add(entity);
 *   await getSyncOperationService().logBatch(ops);
 * });
 * ```
 */
export function markAsInstrumentedWriteContext(): void {
  const trans = Dexie.currentTransaction as unknown as SyncTransaction | undefined;
  if (trans) {
    trans.isInstrumented = true;
  } else {
    console.warn('[SyncImportContext] markAsInstrumentedWriteContext() called outside of transaction');
  }
}
